// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";
import config from "@config";
import { removeNullAndUndefinedProps } from "@transformers/object";

import http from "@http";
import { route } from "@routes/helper";
import actionCreators from "@modules/service/actionCreator";
import {
  GetAlertsList,
  GetAlert,
  GetAlertSchema,
  AddAlert,
  StartDraftAlert,
  UpdateAlert,
  UpdateRecipientAssignments,
  UpdateAlertAssignments,
  DisableAlert,
} from "@modules/service/constants/actions";

import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import type { Alert } from "@modules/service/reducer/alerts/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type AlertListRequest = $Exact<{
  clientId: string,
  isActive: null | boolean,
  page: null | number,
}>;

export function* onGetAlertsList({
  payload,
}: Action<AlertListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<Alert>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration`,
      {
        params: {
          isActive: payload.isActive,
          limit: config.results100PerPage,
        },
      },
    );

    yield put(actionCreators.GetAlertsListSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetAlertsListError(e.response));
  }
}

type AlertRequest = AlertListRequest &
  $Exact<{
    alertId: string,
  }>;

export function* onGetAlert({
  payload,
}: Action<AlertRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Alert>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const stableResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/assignment`,
    );

    yield all([
      put(actionCreators.GetAlertSuccess(stableResponse.data)),
      put(actionCreators.SetAlertAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetAlertError(e.response));
  }
}

type RefreshRequest = $Exact<{
  clientId: string,
  alertId?: string,
  isActive?: boolean,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  yield put(actionCreators.GetAlertsList({ clientId: params.clientId, isActive: params.isActive }));

  if (params.alertId) {
    yield put(
      actionCreators.GetAlert({
        clientId: params.clientId,
        alertId: params.alertId,
        isActive: params.isActive,
      }),
    );
  }
}

// CREATE

type GetAlertSchemaRequest = $Exact<{
  clientId: string,
}>;

export function* onGetAlertSchema({
  payload,
}: Action<GetAlertSchemaRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("AlertConfigurationRevisionDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAlertSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAlertSchemaError(e.response));
  }
}

export function* onStartDraftAlert({
  payload,
}: Action<AlertRequest>): Generator<*, void, { data: ItemResponse<ApprovableContainer<Alert>> }> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
    );

    yield all([
      put(actionCreators.SetAlertAssignments([])),
      put(actionCreators.StartDraftAlertSuccess(response.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDraftAlertError(e.response));
  }
}

type AlertAddRequest = AlertListRequest &
  $Exact<{
    draft: Alert,
    assignments: Array<HierarchyAssignment>,
  }>;

export function* onAddAlert({ payload }: Action<AlertAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, { clientId: payload.clientId, isActive: payload.isActive });

    yield all([
      put(actionCreators.AddAlertSuccess(response.data)),
      put(sendSuccessMessage("SUCCESSFULLY_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddAlertError(e.response));
  }
}

// UPDATE

export function* onUpdateRecipientAssignments(payload: Object): Generator<*, void, *> {
  try {
    if (!payload || !payload.alertId || !payload.clientId || !payload.bespokeRecipients) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { alertId, clientId, bespokeRecipients } = payload;
    let allApiCalls = [];
    bespokeRecipients.map((recipient: Object) => {
      recipient.assignmentRules &&
        recipient.assignmentRules.map((assignmentData: Object) => {
          const url = `${config.endpointBaseUrl}api/v1/service-admin/client/${clientId}/service-config/absence-management/alert/configuration/${alertId}/bespoke-recipient/${recipient.id}/assignment`;

          allApiCalls.push({ url, data: removeNullAndUndefinedProps(assignmentData) });
        });
    });
    const allArr = [
      ...allApiCalls.map(a => {
        return call(http.post, a.url, a.data);
      }),
    ];
    yield all(allArr);

    yield put(actionCreators.UpdateRecipientAssignmentsSuccess());
  } catch (e) {
    yield put(actionCreators.UpdateRecipientAssignmentsError(e.response));
  }
}

type AlertUpdateRequest = AlertRequest &
  $Exact<{
    draft: Alert,
  }>;

export function* onUpdateAlert({ payload }: Action<AlertUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.alertId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const bespokeRecipients = JSON.parse(JSON.stringify(payload.draft.bespokeRecipients)); //Deep clone...

    const draft = {
      ...payload.draft,
      details: {
        ...payload.draft.details,
      },
      bespokeRecipients: payload.draft.bespokeRecipients.map(b => {
        delete b.assignmentRules;
        return b;
      }),
    };

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
      draft,
    );

    yield all([
      put(sendSuccessMessage("SUCCESSFULLY_UPDATED")),
      call(onUpdateRecipientAssignments, {
        clientId: payload.clientId,
        alertId: payload.alertId,
        bespokeRecipients,
      }),
      put(actionCreators.UpdateAlertSuccess(response.data)),
    ]);

    yield call(onRefresh, { clientId: payload.clientId, alertId: payload.alertId });
  } catch (e) {
    yield put(actionCreators.UpdateAlertError(e.response));
  }
}

type AlertUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<Alert>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateAlertAssignments({
  payload,
}: Action<AlertUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const prevAssignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/alert/configuration/${payload.container.id}/assignment`,
    );
    if (prevAssignmentsResponse.data && prevAssignmentsResponse.data.length) {
      yield all([
        ...prevAssignmentsResponse.data.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/alert/configuration/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/alert/configuration/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      providerId: payload.container.id,
    });

    yield put(
      actionCreators.GetAlert({
        clientId: payload.container.clientId,
        alertId: payload.container.id,
      }),
    );
  } catch (e) {
    yield put(actionCreators.UpdateAlertAssignmentsError(e.message));
  }
}

// DISABLE

export type AlertDisableRequest = AlertRequest &
  $Exact<{
    approvable: ApprovableContainer<Alert>,
  }>;

export function* onDisableAlert({
  payload,
}: Action<AlertDisableRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Alert>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.alertId || !payload.approvable) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const draft = payload.approvable.content;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
      {
        ...draft,
        details: {
          ...draft.details,
          activeFrom: draft.details.activeTo ? new Date().toISOString() : draft.details.activeFrom,
          activeTo: draft.details.activeTo ? null : new Date().toISOString(),
        },
      },
    );

    yield all([
      put(actionCreators.DisableAlertSuccess(response.data)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_FEATURES_ALERTS", {
            clientId: payload.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("SUCCESSFULLY_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DisableAlertError(e.response));
  }
}

// ROOT

export function* AlertsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAlertsList, onGetAlertsList),
    takeEvery(GetAlert, onGetAlert),
    takeEvery(GetAlertSchema, onGetAlertSchema),
    takeEvery(AddAlert, onAddAlert),
    takeEvery(StartDraftAlert, onStartDraftAlert),
    takeEvery(UpdateAlert, onUpdateAlert),
    takeEvery(UpdateRecipientAssignments, onUpdateRecipientAssignments),
    takeEvery(UpdateAlertAssignments, onUpdateAlertAssignments),
    takeEvery(DisableAlert, onDisableAlert),
  ]);
}

export default AlertsSagas;
